import { parseISO } from "date-fns";
import { hasSpecificDateAnchor } from "@common/helpers/domains/accounting/scheduleAnchors";
import {
    RecognitionAnchor,
    RecognitionMethod,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import { formatIsoDateForDisplay } from "@helpers/date";
import {
    formatPreviewDateLabel,
    isSourceDateAnchor,
} from "./scheduleRecognitionUi";

export const SCHEDULE_PREVIEW_EXAMPLE_TERM_START = "2026-01-01";

export function resolvePreviewAnchorUsage(
    components: ReadonlyArray<{ anchor: RecognitionAnchor }>,
): { usesSourceDate: boolean; usesSpecificDate: boolean } {
    return {
        usesSourceDate: components.some((component) =>
            isSourceDateAnchor(component.anchor),
        ),
        usesSpecificDate: hasSpecificDateAnchor(components),
    };
}

export function resolvePreviewTermDates(options: {
    sourceDate: string | undefined;
    specificDate: string | undefined;
    components: ReadonlyArray<{ anchor: RecognitionAnchor }>;
}): { termStart: string; specificDate: string | undefined } {
    const sourceDate = options.sourceDate?.trim();
    const specificDate = options.specificDate?.trim();
    const hasSourceDate = sourceDate != null && sourceDate !== "";
    const hasSpecificDate = specificDate != null && specificDate !== "";
    const { usesSourceDate, usesSpecificDate } = resolvePreviewAnchorUsage(
        options.components,
    );
    const termStart =
        usesSourceDate && hasSourceDate
            ? sourceDate
            : SCHEDULE_PREVIEW_EXAMPLE_TERM_START;
    if (!usesSpecificDate) {
        return { termStart, specificDate: undefined };
    }
    return {
        termStart,
        specificDate: hasSpecificDate ? specificDate : termStart,
    };
}

const METHOD_DESCRIPTIONS: Record<RecognitionMethod, string> = {
    [RecognitionMethod.straightLine]: "Straight-line recognition",
    [RecognitionMethod.dailyProRata]: "Daily pro-rata recognition",
    [RecognitionMethod.pointInTime]: "Point-in-time recognition",
    [RecognitionMethod.milestone]: "Milestone recognition",
};

function roundToCents(value: number): number {
    const rounded = Math.round(value * 100) / 100;
    return rounded === 0 ? 0 : rounded;
}

export interface PreviewLadderRow {
    rowKey: string;
    periodLabel: string;
    description: string;
    recognized: number;
    balance: number;
    milestone?: PreviewMilestoneRowInfo;
}

export interface PreviewMilestoneRowInfo {
    number: number;
    name: string;
}

interface PeriodLabelContext {
    termUnit: ScheduleTermUnit;
    sourceDate: string | undefined;
    specificDate: string | undefined;
    anchorsByComponentOrder: ReadonlyArray<{
        componentOrder: number;
        anchor: RecognitionAnchor;
    }>;
}

export function toAnchorsByComponentOrder(
    components: ReadonlyArray<{ anchor: RecognitionAnchor }>,
): PeriodLabelContext["anchorsByComponentOrder"] {
    return components.map((component, componentOrder) => ({
        componentOrder,
        anchor: component.anchor,
    }));
}

function describeMilestoneRow(options: {
    componentOrder: number;
    milestoneNames?: ReadonlyArray<{ name: string }>;
}): PreviewMilestoneRowInfo {
    const name = options.milestoneNames?.[options.componentOrder - 1]?.name;
    return {
        number: options.componentOrder,
        name:
            name == null || name === ""
                ? `Milestone ${options.componentOrder}`
                : name,
    };
}

export function buildPreviewLadder(options: {
    rows: Array<{
        periodKey: string;
        effectiveDate?: string;
        amount: number;
        componentOrder: number;
        method: RecognitionMethod;
    }>;
    totalAmount: number;
    periodLabelContext?: PeriodLabelContext;
    milestoneNames?: ReadonlyArray<{ name: string }>;
}): PreviewLadderRow[] {
    const methodRank: Record<RecognitionMethod, number> = {
        [RecognitionMethod.pointInTime]: 0,
        [RecognitionMethod.milestone]: 1,
        [RecognitionMethod.straightLine]: 2,
        [RecognitionMethod.dailyProRata]: 2,
    };
    const periodKeys = [
        ...new Set(options.rows.map((row) => row.periodKey)),
    ].sort((leftKey, rightKey) => leftKey.localeCompare(rightKey));
    const periodNumbersByKey = new Map(
        periodKeys.map((periodKey, periodIndex) => [
            periodKey,
            periodIndex + 1,
        ]),
    );
    const labeledRows = options.rows.map((row) => ({
        row,
        periodNumber: periodNumbersByKey.get(row.periodKey) ?? 1,
        sortTime: parseISO(
            row.method === RecognitionMethod.milestone && row.effectiveDate
                ? row.effectiveDate
                : row.periodKey,
        ).getTime(),
    }));
    const sortedRows = labeledRows.sort(
        (leftRow, rightRow) =>
            leftRow.sortTime - rightRow.sortTime ||
            methodRank[leftRow.row.method] - methodRank[rightRow.row.method] ||
            leftRow.row.componentOrder - rightRow.row.componentOrder,
    );

    let remaining = options.totalAmount;
    return sortedRows.map(({ row, periodNumber }) => {
        remaining = roundToCents(remaining - row.amount);
        if (row.method === RecognitionMethod.milestone && row.effectiveDate) {
            const milestone = describeMilestoneRow({
                componentOrder: row.componentOrder,
                milestoneNames: options.milestoneNames,
            });
            return {
                rowKey: `${row.periodKey}-${row.componentOrder}`,
                periodLabel: formatIsoDateForDisplay(row.effectiveDate),
                description: milestone.name,
                recognized: row.amount,
                balance: remaining,
                milestone,
            };
        }
        return {
            rowKey: `${row.periodKey}-${row.componentOrder}`,
            periodLabel: resolvePeriodLabel({
                periodKey: row.periodKey,
                periodNumber,
                componentOrder: row.componentOrder,
                method: row.method,
                periodLabelContext: options.periodLabelContext,
            }),
            description: METHOD_DESCRIPTIONS[row.method],
            recognized: row.amount,
            balance: remaining,
        };
    });
}

function resolveComponentExampleStartDate(options: {
    componentOrder: number;
    periodLabelContext: PeriodLabelContext;
}): string | undefined {
    const componentAnchor =
        options.periodLabelContext.anchorsByComponentOrder.find(
            (component) => component.componentOrder === options.componentOrder,
        )?.anchor;
    if (componentAnchor === RecognitionAnchor.specificDate) {
        return options.periodLabelContext.specificDate;
    }
    return options.periodLabelContext.sourceDate;
}

function periodKeyToDate(periodKey: string): string {
    if (periodKey.length === 4) {
        return `${periodKey}-01-01`;
    }
    if (periodKey.length === 7) {
        return `${periodKey}-01`;
    }
    return periodKey;
}

function resolvePeriodLabel(options: {
    periodKey: string;
    periodNumber: number;
    componentOrder: number;
    method: RecognitionMethod;
    periodLabelContext?: PeriodLabelContext;
}): string {
    const periodLabelContext = options.periodLabelContext;
    if (periodLabelContext == null) {
        return `Period ${options.periodNumber}`;
    }

    const exampleStartDate = resolveComponentExampleStartDate({
        componentOrder: options.componentOrder,
        periodLabelContext,
    });
    if (exampleStartDate == null || exampleStartDate === "") {
        return `Period ${options.periodNumber}`;
    }

    return formatPreviewDateLabel({
        date: parseISO(periodKeyToDate(options.periodKey)),
        termUnit:
            options.method === RecognitionMethod.pointInTime &&
            periodLabelContext.termUnit === ScheduleTermUnit.weeks
                ? ScheduleTermUnit.days
                : periodLabelContext.termUnit,
    });
}
